/**
 * Safe timeout and interval hooks with automatic cleanup on unmount.
 *
 * These hooks prevent memory leaks and "Can't perform a React state update
 * on an unmounted component" warnings by automatically clearing timers
 * when the component unmounts.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { setTimeout } = useSafeTimeout();
 *
 *   const handleClick = () => {
 *     setTimeout(() => {
 *       // Safe to update state here - won't run if component unmounts
 *       setData(newData);
 *     }, 3000);
 *   };
 *
 *   return <button onClick={handleClick}>Start</button>;
 * }
 * ```
 */

import { useCallback, useEffect, useRef } from "react";

// =============================================================================
// useSafeTimeout - setTimeout with automatic cleanup
// =============================================================================

/**
 * Hook that provides a safe setTimeout that automatically clears on unmount.
 *
 * @returns Object with setTimeout and clearAllTimeouts functions
 *
 * @example
 * ```tsx
 * const { setTimeout, clearAllTimeouts } = useSafeTimeout();
 *
 * // Set a timeout - automatically cleared on unmount
 * const id = setTimeout(() => setVisible(false), 3000);
 *
 * // Optionally clear manually
 * clearTimeout(id);
 *
 * // Or clear all at once
 * clearAllTimeouts();
 * ```
 */
export function useSafeTimeout() {
  const timeoutIds = useRef<Set<ReturnType<typeof globalThis.setTimeout>>>(
    new Set()
  );
  const isMounted = useRef(true);

  // Clear all timeouts on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      timeoutIds.current.forEach((id) => globalThis.clearTimeout(id));
      timeoutIds.current.clear();
    };
  }, []);

  const safeSetTimeout = useCallback(
    (callback: () => void, delay: number): ReturnType<typeof globalThis.setTimeout> => {
      const id = globalThis.setTimeout(() => {
        timeoutIds.current.delete(id);
        // Only execute if still mounted
        if (isMounted.current) {
          callback();
        }
      }, delay);
      timeoutIds.current.add(id);
      return id;
    },
    []
  );

  const clearTimeout = useCallback(
    (id: ReturnType<typeof globalThis.setTimeout>) => {
      globalThis.clearTimeout(id);
      timeoutIds.current.delete(id);
    },
    []
  );

  const clearAllTimeouts = useCallback(() => {
    timeoutIds.current.forEach((id) => globalThis.clearTimeout(id));
    timeoutIds.current.clear();
  }, []);

  return {
    setTimeout: safeSetTimeout,
    clearTimeout,
    clearAllTimeouts,
  };
}

// =============================================================================
// useSafeInterval - setInterval with automatic cleanup
// =============================================================================

/**
 * Hook that provides a safe setInterval that automatically clears on unmount.
 *
 * @returns Object with setInterval and clearInterval functions
 *
 * @example
 * ```tsx
 * const { setInterval, clearInterval } = useSafeInterval();
 *
 * useEffect(() => {
 *   const id = setInterval(() => {
 *     refetch();
 *   }, 5000);
 *
 *   return () => clearInterval(id);
 * }, []);
 * ```
 */
export function useSafeInterval() {
  const intervalIds = useRef<Set<ReturnType<typeof globalThis.setInterval>>>(
    new Set()
  );
  const isMounted = useRef(true);

  // Clear all intervals on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      intervalIds.current.forEach((id) => globalThis.clearInterval(id));
      intervalIds.current.clear();
    };
  }, []);

  const safeSetInterval = useCallback(
    (
      callback: () => void,
      delay: number
    ): ReturnType<typeof globalThis.setInterval> => {
      const id = globalThis.setInterval(() => {
        // Only execute if still mounted
        if (isMounted.current) {
          callback();
        }
      }, delay);
      intervalIds.current.add(id);
      return id;
    },
    []
  );

  const clearInterval = useCallback(
    (id: ReturnType<typeof globalThis.setInterval>) => {
      globalThis.clearInterval(id);
      intervalIds.current.delete(id);
    },
    []
  );

  return {
    setInterval: safeSetInterval,
    clearInterval,
  };
}

// =============================================================================
// useDelayedState - state that resets after delay
// =============================================================================

/**
 * Hook for state that automatically resets to initial value after a delay.
 * Useful for temporary UI states like "loading", "success", "copied", etc.
 *
 * @param initialValue - The initial/reset value
 * @param resetDelay - Delay in ms before resetting (default 3000)
 *
 * @example
 * ```tsx
 * const [isAdding, setIsAdding] = useDelayedState(false, 3000);
 *
 * const handleAdd = () => {
 *   setIsAdding(true); // Will auto-reset to false after 3s
 *   performAction();
 * };
 * ```
 */
export function useDelayedState<T>(
  initialValue: T,
  resetDelay = 3000
): [T, (value: T) => void] {
  const [state, setStateInternal] = React.useState(initialValue);
  const { setTimeout, clearAllTimeouts } = useSafeTimeout();

  const setState = useCallback(
    (value: T) => {
      clearAllTimeouts();
      setStateInternal(value);
      if (value !== initialValue) {
        setTimeout(() => setStateInternal(initialValue), resetDelay);
      }
    },
    [initialValue, resetDelay, setTimeout, clearAllTimeouts]
  );

  return [state, setState];
}

// Need React import for useDelayedState
import React from "react";

// =============================================================================
// useSequentialTimeouts - run multiple timeouts in sequence
// =============================================================================

/**
 * Hook for running a sequence of callbacks at specified delays.
 * All timeouts are automatically cleaned up on unmount.
 *
 * @example
 * ```tsx
 * const runSequence = useSequentialTimeouts();
 *
 * const startAnimation = () => {
 *   runSequence([
 *     { delay: 0, callback: () => setPhase("start") },
 *     { delay: 500, callback: () => setShowLogo(true) },
 *     { delay: 1000, callback: () => setPhase("middle") },
 *     { delay: 1500, callback: () => onComplete() },
 *     { delay: 2000, callback: () => setPhase("done") },
 *   ]);
 * };
 * ```
 */
export function useSequentialTimeouts() {
  const { setTimeout, clearAllTimeouts } = useSafeTimeout();

  const runSequence = useCallback(
    (steps: Array<{ delay: number; callback: () => void }>) => {
      clearAllTimeouts();
      for (const step of steps) {
        setTimeout(step.callback, step.delay);
      }
    },
    [setTimeout, clearAllTimeouts]
  );

  return runSequence;
}
